// Security utilities for sanitizing user inputs before sending to AI.
// Prevents prompt injection via job description and question fields.

/**
 * Strips common prompt injection patterns from user input.
 * This runs client-side before sending to the edge function.
 * The edge function should also have its own server-side checks.
 */

// Patterns that indicate prompt injection attempts
const INJECTION_PATTERNS = [
  // Direct instruction overrides
  /ignore\s+(all\s+)?previous\s+instructions/gi,
  /ignore\s+(all\s+)?above\s+instructions/gi,
  /disregard\s+(all\s+)?previous/gi,
  /forget\s+(all\s+)?previous/gi,
  /override\s+(all\s+)?instructions/gi,
  /new\s+instructions?\s*:/gi,

  // System prompt manipulation
  /you\s+are\s+now\s+a/gi,
  /act\s+as\s+(a\s+)?/gi,
  /pretend\s+(to\s+be|you\s+are)/gi,
  /your\s+new\s+(role|purpose|instructions?)\s*(is|are)?/gi,
  /system\s*:\s*/gi,
  /\[system\]/gi,
  /\[instruction\]/gi,

  // Output manipulation
  /do\s+not\s+follow\s+(the\s+)?(above|previous)/gi,
  /instead\s*,?\s*(just|simply)?\s*(output|print|return|say)/gi,
  /repeat\s+(the\s+)?(system|initial)\s+(prompt|instructions?)/gi,
  /reveal\s+(the\s+)?(system|initial)\s+(prompt|instructions?)/gi,
  /what\s+(are|is)\s+(your|the)\s+(system\s+)?(prompt|instructions?)/gi,

  // Data exfiltration
  /send\s+(this|the\s+data|my\s+data)\s+to/gi,
  /forward\s+(this|the\s+data)\s+to/gi,
  /make\s+a\s+(http|api)\s+request/gi,
  /fetch\s*\(/gi,
  /curl\s+/gi,

  // Encoded injection attempts
  /&#x[0-9a-f]+;/gi,
  /\\u[0-9a-f]{4}/gi,
];

/**
 * Checks if input contains potential prompt injection.
 * Returns { safe: boolean, cleaned: string }
 */
export function sanitizeAiInput(input: string): {
  safe: boolean;
  cleaned: string;
  flagged: boolean;
} {
  let cleaned = input;
  let flagged = false;

  for (const pattern of INJECTION_PATTERNS) {
    if (pattern.test(cleaned)) {
      flagged = true;
      // Reset lastIndex for global patterns
      pattern.lastIndex = 0;
      // Remove the injection attempt
      cleaned = cleaned.replace(pattern, "[removed]");
    }
  }

  // Also strip excessive special characters that might be encoding tricks
  // but preserve normal punctuation
  cleaned = cleaned
    .replace(/[<>{}]/g, "") // Strip HTML/template brackets
    .replace(/\0/g, "") // Strip null bytes
    .trim();

  return {
    safe: !flagged,
    cleaned,
    flagged,
  };
}

/**
 * Validates that input looks like a job description, not an instruction set.
 * Simple heuristic — real JDs have certain characteristics.
 */
export function looksLikeJobDescription(input: string): boolean {
  const lower = input.toLowerCase();
  const wordCount = input.split(/\s+/).filter(Boolean).length;

  // Too short to be a real JD
  if (wordCount < 15) return false;

  // Check for common JD keywords (at least 2 should be present)
  const jdKeywords = [
    "experience",
    "responsibilities",
    "requirements",
    "qualifications",
    "skills",
    "role",
    "position",
    "team",
    "salary",
    "benefits",
    "apply",
    "candidate",
    "hire",
    "job",
    "work",
    "company",
    "about us",
    "we are looking",
    "you will",
    "must have",
    "preferred",
    "degree",
    "years",
    "full-time",
    "part-time",
    "remote",
    "hybrid",
    "onsite",
  ];

  const matchCount = jdKeywords.filter((kw) => lower.includes(kw)).length;
  return matchCount >= 2;
}
